import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  UserGroupIcon,
  AcademicCapIcon,
  ClockIcon,
  EnvelopeIcon,
  PhoneIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  CheckCircleIcon,
  XCircleIcon
} from '@heroicons/react/24/solid';

export default function TutorApplicationsList() {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchApplications();
  }, []);

  useEffect(() => {
    filterApplications();
  }, [statusFilter, searchTerm, applications]);

  const fetchApplications = async () => {
    try {
      const { data } = await axios.get('/api/admin/tutor-applications');
      console.log('Tutor applications data:', data); // Debug log
      // Backend returns { applications: [...], total, offset, limit }
      const applicationsArray = data.applications || data || [];
      setApplications(Array.isArray(applicationsArray) ? applicationsArray : []);
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error(error.response?.data?.error || 'Failed to load tutor applications');
      setApplications([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const filterApplications = () => {
    let filtered = [...applications];

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === statusFilter);
    }

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(app =>
        app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.subjects_can_teach?.some(subject =>
          subject.toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    setFilteredApplications(filtered);
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      reviewing: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      interview_scheduled: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      approved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      rejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      withdrawn: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
    };

    const icons = {
      approved: <CheckCircleIcon className="h-4 w-4 inline mr-1" />,
      rejected: <XCircleIcon className="h-4 w-4 inline mr-1" />
    };

    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${styles[status] || styles.pending}`}>
        {icons[status]}
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Tutor Applications
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Review and approve tutor applications
        </p>
      </div>

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-6 border border-transparent dark:border-gray-700">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Search */}
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, or subject..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 hidden xl:block" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full pl-4 xl:pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="withdrawn">Withdrawn</option>
            </select>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {applications.filter(a => a.status === 'pending').length}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Pending</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {applications.filter(a => a.status === 'approved').length}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Approved</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {applications.filter(a => a.status === 'rejected').length}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Rejected</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {applications.length}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">Total</div>
          </div>
        </div>
      </div>

      {/* Applications List */}
      {filteredApplications.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-12 text-center border border-transparent dark:border-gray-700">
          <UserGroupIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            No applications found
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            {searchTerm || statusFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'New tutor applications will appear here'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredApplications.map(application => (
            <div
              key={application.id}
              onClick={() => navigate(`/admin/applications/${application.id}`)}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 hover:shadow-lg transition cursor-pointer border border-transparent dark:border-gray-700"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                      {application.name}
                    </h3>
                    {getStatusBadge(application.status)}
                  </div>

                  <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-1">
                      <EnvelopeIcon className="h-4 w-4" />
                      {application.email}
                    </div>
                    <div className="flex items-center gap-1">
                      <PhoneIcon className="h-4 w-4" />
                      {application.phone}
                    </div>
                    <div className="flex items-center gap-1">
                      <ClockIcon className="h-4 w-4" />
                      {new Date(application.created_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Qualification</div>
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {application.highest_qualification}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {application.field_of_study}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Experience</div>
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {application.years_experience}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Availability</div>
                  <div className="font-semibold text-gray-900 dark:text-white">
                    {application.hours_per_week}
                  </div>
                </div>
              </div>

              {application.subjects_can_teach && application.subjects_can_teach.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Can Teach</div>
                  <div className="flex flex-wrap gap-2">
                    {application.subjects_can_teach.map((subject, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded text-xs font-semibold"
                      >
                        {subject}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {application.grade_levels_can_teach && application.grade_levels_can_teach.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs text-gray-500 dark:text-gray-400 mb-2">Grade Levels</div>
                  <div className="flex flex-wrap gap-2">
                    {application.grade_levels_can_teach.slice(0, 4).map((grade, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs"
                      >
                        {grade}
                      </span>
                    ))}
                    {application.grade_levels_can_teach.length > 4 && (
                      <span className="px-2 py-1 text-gray-500 dark:text-gray-400 text-xs">
                        +{application.grade_levels_can_teach.length - 4} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {application.user_id && (
                <div className="mt-4 p-3 bg-green-50 dark:bg-green-900 rounded-lg flex items-center gap-2 text-sm text-green-800 dark:text-green-200">
                  <CheckCircleIcon className="h-5 w-5" />
                  Tutor account created on {new Date(application.account_created_at).toLocaleDateString()}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
